import { Layout, PageHero } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Timeline } from "../components/Timeline";
import { Leadership, CTA, Numbers } from "../components/Sections";
import { Reveal } from "../components/motion";
import {
  ABOUT_VALUES, TAGLINE, COMPANY_PURPOSE, COMPANY_VISION,
  COMPANY_MISSION, COMPANY_CORE_THOUGHT, LEGAL_ENTITY, SOCIAL_LINKS
} from "../data";
import { Target, Eye, Compass, Sparkles, Lightbulb, Linkedin, Instagram, Github, MapPin } from "lucide-react";

export default function About() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "NeuraVolt Technology",
    "legalName": LEGAL_ENTITY,
    "url": "https://neuravolt.in/about",
    "slogan": TAGLINE,
    "description": COMPANY_CORE_THOUGHT,
    "sameAs": [
      SOCIAL_LINKS.linkedin,
      SOCIAL_LINKS.linkedin_profile,
      SOCIAL_LINKS.instagram,
      SOCIAL_LINKS.github,
      SOCIAL_LINKS.google_maps,
    ],
    "knowsAbout": [
      "Artificial Intelligence",
      "Enterprise Software Engineering",
      "Connected Digital Ecosystems",
      "Cloud Infrastructure",
      "IoT Automation"
    ]
  };

  return (
    <Layout>
      <Seo
        title="About Us - Intelligent Solutions. Measurable Impact."
        description={`NeuraVolt (${LEGAL_ENTITY}) brings fragmented technology together into one connected ecosystem. ${COMPANY_CORE_THOUGHT}`}
        keywords="about Neuravolt, VOLTNOVATE SOLUTIONS LLP, connected technology ecosystem, AI first software company India, enterprise engineering, Startup India DPIIT"
        path="/about"
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="ABOUT NEURAVOLT"
        title="One Connected Ecosystem for Modern Technology"
        sub={`${TAGLINE} Recognized under Startup India (DPIIT) & incorporated as ${LEGAL_ENTITY}.`}
      />

      {/* Strategic Foundation: Core Thought, Purpose, Vision & Mission */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          {/* Core Thought Featured Banner */}
          <Reveal>
            <div className="gborder glass-card-dark bg-[#0B0B0C] rounded-3xl p-8 sm:p-12 border border-[#FF5A1F]/35 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] relative overflow-hidden">
              <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-[#FF5A1F]/15 blur-[130px] pointer-events-none" />
              
              <div className="relative z-10 max-w-4xl">
                <div className="flex flex-wrap items-center gap-2.5 mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#FF5A1F]/20 text-[#FF5A1F] border border-[#FF5A1F]/40 flex items-center gap-1.5">
                    <Lightbulb className="w-3.5 h-3.5" /> Core Thought
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-neutral-300 border border-white/15">
                    {TAGLINE}
                  </span>
                </div>

                <blockquote className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-snug tracking-tight">
                  {COMPANY_CORE_THOUGHT}
                </blockquote>
              </div>
            </div>
          </Reveal>

          {/* 3 Pillars: Purpose, Vision & Mission */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Purpose */}
            <Reveal delay={0.1}>
              <div className="rounded-3xl p-8 sm:p-9 bg-white border border-black/10 shadow-lg hover:shadow-xl hover:border-[#FF5A1F]/40 transition-all h-full flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-[#FF5A1F]/10 flex items-center justify-center text-[#FF5A1F] mb-6">
                    <Compass className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-mono uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">
                    Our Purpose
                  </div>
                  <h3 className="font-display text-2xl font-bold text-neutral-900 mb-4">
                    Connect Fragmented Tech
                  </h3>
                  <p className="text-neutral-600 leading-relaxed text-base">
                    {COMPANY_PURPOSE}
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Vision */}
            <Reveal delay={0.2}>
              <div className="rounded-3xl p-8 sm:p-9 bg-white border border-black/10 shadow-lg hover:shadow-xl hover:border-[#FF5A1F]/40 transition-all h-full flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 mb-6">
                    <Eye className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-mono uppercase tracking-widest text-blue-600 font-bold mb-2">
                    Our Vision
                  </div>
                  <h3 className="font-display text-2xl font-bold text-neutral-900 mb-4">
                    Global Integrated Growth
                  </h3>
                  <p className="text-neutral-600 leading-relaxed text-base">
                    {COMPANY_VISION}
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Mission */}
            <Reveal delay={0.3}>
              <div className="rounded-3xl p-8 sm:p-9 bg-white border border-black/10 shadow-lg hover:shadow-xl hover:border-[#FF5A1F]/40 transition-all h-full flex flex-col justify-between">
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 mb-6">
                    <Target className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-mono uppercase tracking-widest text-emerald-600 font-bold mb-2">
                    Our Mission
                  </div>
                  <h3 className="font-display text-2xl font-bold text-neutral-900 mb-4">
                    Continuous Innovation
                  </h3>
                  <p className="text-neutral-600 leading-relaxed text-base">
                    {COMPANY_MISSION}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="pb-16 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-mono uppercase tracking-wider text-[#FF5A1F] font-semibold">
              HOW WE OPERATE
            </span>
            <h2 className="font-display text-3xl font-bold text-[#0B0B0B] mt-1">
              Core Architectural Values
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ABOUT_VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 4) * 0.07}>
                <div className="rounded-3xl p-7 border border-black/5 bg-[#FAF9F6] hover:border-[#FF5A1F]/30 hover:shadow-md transition-all h-full">
                  <div className="font-display text-xl font-bold text-[#FF5A1F] mb-2">{v.title}</div>
                  <p className="text-neutral-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Numbers />
      <Timeline />
      <Leadership />
      <CTA />
    </Layout>
  );
}
